import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch, faTimes } from "@fortawesome/free-solid-svg-icons";

/**
 * Reusable search input component with debouncing
 *
 * Features:
 * - Debounced input to reduce API calls
 * - Clear button when input has value
 * - Keyboard navigation support
 * - Accessible with proper ARIA labels
 */
const SearchInput = ({
  value,
  onChange,
  onClear,
  placeholder = "Search users...",
  debounceMs = 300,
  autoFocus = false,
  className = "",
  inputClassName = "",
  minLength = 2,
  ...rest
}) => {
  const [localValue, setLocalValue] = useState(value || "");
  const debounceTimerRef = useRef(null);
  const inputRef = useRef(null);

  // Auto focus if requested
  useEffect(() => {
    if (autoFocus && inputRef.current) {
      inputRef.current.focus();
    }
  }, [autoFocus]);

  // Sync with external value changes
  useEffect(() => {
    setLocalValue(value || "");
  }, [value]);

  // Handle input change with debouncing
  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setLocalValue(newValue);

    // Clear existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Set new timer for debounced callback
    debounceTimerRef.current = setTimeout(() => {
      if (onChange) {
        onChange(newValue);
      }
    }, debounceMs);
  };

  // Handle clear button
  const handleClear = () => {
    setLocalValue("");
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    if (onClear) {
      onClear();
    }
    if (onChange) {
      onChange("");
    }
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  const showClearButton = localValue.length > 0;

  return (
    <div className={`relative ${className}`}>
      {/* Search Icon */}
      <div className="absolute left-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
        <FontAwesomeIcon icon={faSearch} className="text-medium-grey" />
      </div>

      {/* Input Field */}
      <input
        ref={inputRef}
        type="text"
        value={localValue}
        onChange={handleInputChange}
        placeholder={placeholder}
        className={`
          w-full pl-10 pr-10 py-2 
          bg-white border border-light-grey rounded-lg
          text-dark-grey placeholder-medium-grey
          focus:outline-none focus:ring-2 focus:ring-gold focus:border-transparent
          transition-all duration-200
          ${inputClassName}
        `}
        aria-label="Search for users"
        aria-describedby={minLength > 0 ? "search-hint" : undefined}
        {...rest}
      />

      {/* Clear Button */}
      {showClearButton && (
        <button
          type="button"
          onClick={handleClear}
          className="
            absolute right-3 top-1/2 transform -translate-y-1/2
            text-medium-grey hover:text-dark-grey
            transition-colors duration-200
            p-1 rounded-full hover:bg-light-grey
          "
          aria-label="Clear search"
        >
          <FontAwesomeIcon icon={faTimes} className="text-sm" />
        </button>
      )}

      {/* Screen reader hint */}
      {minLength > 0 && (
        <span id="search-hint" className="sr-only">
          Enter at least {minLength} characters to search
        </span>
      )}
    </div>
  );
};

SearchInput.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func,
  onClear: PropTypes.func,
  placeholder: PropTypes.string,
  debounceMs: PropTypes.number,
  autoFocus: PropTypes.bool,
  className: PropTypes.string,
  inputClassName: PropTypes.string,
  minLength: PropTypes.number,
};

export default SearchInput;
